"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, Leaf, Scale, BookOpen, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const pillars = [{
  id: "dia-ly-phong-thuy",
  title: "Khoa học Địa lý Phong thủy",
  subtitle: "Nghiên cứu quy luật tương tác không gian và môi trường sống",
  icon: Compass,
  description: "Tiếp cận phong thủy dưới góc nhìn khoa học địa lý, phân tích hình thái địa hình, khí hậu, thổ nhưỡng và trường khí tự nhiên nhằm tối ưu hóa tổ chức không gian định cư và kiến trúc bền vững.",
  legalBasis: "Quy chuẩn học thuật VAEG"
}, {
  id: "moi-truong-ben-vung",
  title: "Môi trường & Phát triển bền vững",
  subtitle: "Bảo vệ tài nguyên, thích ứng biến đổi khí hậu và sinh thái",
  icon: Leaf,
  description: "Tập trung đánh giá tác động môi trường, bảo tồn đa dạng sinh học, ứng phó biến đổi khí hậu và chuyển dịch xanh, đảm bảo cân bằng sinh thái phục vụ phát triển kinh tế - xã hội lâu dài.",
  legalBasis: "Định hướng chiến lược quốc gia"
}];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const transitionSettings = {
    duration: shouldReduceMotion ? 0.05 : 0.38,
    ease: [0.16, 1, 0.3, 1] as const
  };
  return <section id="giới-thiệu-chung" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-8 max-w-5xl">
        {/* Institutional Eyebrow & Mandate Indicator */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={transitionSettings} className="flex flex-col items-center text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-sm bg-accent text-accent-foreground text-xs md:text-sm font-semibold tracking-wider uppercase border border-border">
            <Scale className="size-3.5" />
            <span>Tôn chỉ & Trách nhiệm chuyên môn</span>
          </div>

          {/* Exact dictated copy */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-secondary tracking-tight">
            Lĩnh vực hoạt động
          </h1>

          {/* Archival Double-Rule Motif */}
          <div className="mt-5 mb-6 flex flex-col items-center w-full max-w-xs">
            <div className="h-0.5 w-16 bg-secondary rounded-full mb-1" />
            <Separator className="w-full bg-border" />
          </div>

          {/* Lead Paragraph */}
          <p className="max-w-3xl text-base md:text-lg text-muted-foreground leading-relaxed">
            Hội Địa lý Môi trường Việt Nam (VAEG) triển khai đồng bộ và hài hòa
            giữa hai trọng tâm chuyên môn: Khoa học Địa lý Phong thủy và Môi
            trường - Phát triển bền vững. Cả hai định hướng được xác định với vai
            trò ngang hàng, hỗ trợ lẫn nhau trong nghiên cứu khoa học, phản biện
            chính sách và phụng sự cộng đồng.
          </p>
        </motion.div>

        {/* Dual Pillar Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-10">
          {pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.id} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: shouldReduceMotion ? 0.05 : 0.35,
            delay: shouldReduceMotion ? 0 : i * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }}>
                <Card className="h-full bg-card text-card-foreground border border-border rounded-sm shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200 flex flex-col justify-between overflow-hidden">
                  <div className="h-1 bg-primary w-full" />
                  <CardContent className="p-6 md:p-8 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="size-11 rounded-sm bg-accent flex items-center justify-center text-primary">
                          <IconComponent className="size-6" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase px-2.5 py-1 rounded-sm bg-muted border border-border">
                          {pillar.legalBasis}
                        </span>
                      </div>

                      <h2 className="text-xl md:text-2xl font-default font-semibold text-foreground tracking-tight mb-2">
                        {pillar.title}
                      </h2>

                      <p className="text-sm font-medium text-secondary mb-4">
                        {pillar.subtitle}
                      </p>

                      <Separator className="mb-4 bg-border" />

                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>

                    <div className="pt-6 mt-4 border-t border-border flex items-center justify-between">
                      <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                        <BookOpen className="size-3.5 text-primary" />
                        Trọng tâm định hướng
                      </span>
                      <Button variant="ghost" size="sm" asChild className="text-primary hover:text-primary hover:bg-accent px-2 text-xs font-semibold">
                        <Link to="#gioi-thieu-chi-tiet">
                          <span>Chi tiết</span>
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Institutional Citation Note */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: shouldReduceMotion ? 0.05 : 0.4,
        delay: 0.2
      }} className="mt-12 p-4 md:p-5 rounded-sm bg-muted/60 border border-border text-center">
          <p className="text-xs md:text-sm text-muted-foreground leading-normal">
            Hoạt động tuân thủ Điều lệ được Bộ Nội vụ phê duyệt tại Quyết định số
            1157/QĐ-BNV và quyết định thành lập số 483/QĐ-BNV.
          </p>
        </motion.div>
      </div>
    </section>;
}